import React from 'react';
import { generatePath } from 'react-router-dom';
import PbxMenuIcon from 'flow/components/pbx/PbxMenuIcon';
import RouteLink from 'flow/components/ui-components/RouteLink';
import Skeleton from 'flow/components/ui-components/Skeleton';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppSelector } from 'flow/hooks/useRedux';
import { selectOmniTicketQueueMappings } from 'flow/selectors/queue-selectors';
import { QueueKey } from 'flow/types/EntityKeys';
import { routes } from 'flow/types/routes';
import { getOmnichannelChannelTypeIconKey } from 'flow/utils/omnichannel-utils';

interface Props {
    queueKey: QueueKey;
}

const OmniChannelMappings = ({ queueKey }: Props) => {
    const { i18next } = useAppContext();

    const omniChannelMappings = useAppSelector((state) =>
        selectOmniTicketQueueMappings(state, queueKey),
    );

    if (!omniChannelMappings?.size) {
        return (
            <div className="rounded-sm bg-gray-100 p-2">
                <Skeleton>
                    <Skeleton.Avatar className="rounded-lg" />
                    <div className="flex flex-1 flex-col gap-2 pt-[0.6rem] pb-1">
                        <Skeleton.Text className="h-4 w-80" />
                    </div>
                </Skeleton>
            </div>
        );
    }

    return (
        <>
            {omniChannelMappings
                .groupBy((mapping) => mapping.get('channelType'))
                .map((channelMappings) => {
                    const firstMapping = channelMappings.first();
                    const displayExternalReferenceName =
                        firstMapping.get('externalReferenceDisplayName') ||
                        firstMapping.get('externalReferenceId');

                    const numMappings = channelMappings.size - 1;
                    const channelType = firstMapping.get('channelType');

                    const iconKey =
                        getOmnichannelChannelTypeIconKey(channelType);

                    return (
                        <RouteLink
                            key={`rl-${channelType}`}
                            to={generatePath(
                                routes['pbx.queues.queueKey.connections'],
                                {
                                    queueKey,
                                    channelType,
                                },
                            )}
                        >
                            <div className="flex items-center gap-x-3">
                                {iconKey ? (
                                    <PbxMenuIcon iconKey={iconKey} />
                                ) : null}
                                <span className="text-gray-800">
                                    <span className="me-2">
                                        {displayExternalReferenceName}
                                    </span>

                                    {numMappings > 0 && (
                                        <span className="font-bold text-gray-800">
                                            {i18next.t('calls.queue.more', {
                                                number: numMappings,
                                            })}
                                        </span>
                                    )}
                                </span>
                            </div>
                        </RouteLink>
                    );
                })
                .toList()}
        </>
    );
};

export default React.memo(OmniChannelMappings);
